      
// Theme scales, low → high intensity.
export const DENSITY_COLORS = ['#FEDF89', '#FDB022', '#F79009', '#F04438', '#D92D20']
export const ACTIVITY_COLORS = ['#A6F4C5', '#6CE9A6', '#32D583', '#12B76A', '#039855']
export const RISK_COLORS = ['#FECDCA', '#FDA29B', '#F97066', '#F04438', '#B42318']

const RADIUS = 60 // blur radius per point, in screen px
const POINT_ALPHA = 0.2 // how much one point adds to the intensity

const buildPalette = (colors) => {
  const canvas = document.createElement('canvas')
  canvas.width = 256
  canvas.height = 1
  const ctx = canvas.getContext('2d')
  const gradient = ctx.createLinearGradient(0, 0, 256, 0)
  colors.forEach((color, i) =>
    gradient.addColorStop(i / (colors.length - 1), color),
  )
  ctx.fillStyle = gradient
  ctx.fillRect(0, 0, 256, 1)
  return ctx.getImageData(0, 0, 256, 1).data
}

// Smooth heatmap drawn on a canvas covering the visible map: each point adds a
// blurred alpha blob scaled by its weight (0–1, default 1), then the summed
// alpha is colorized with `colors`.
export const createHeatmap = (map, points, colors = DENSITY_COLORS) => {
  const { maps } = window.google

  class Heatmap extends maps.OverlayView {
    onAdd() {
      this.palette = buildPalette(colors)
      this.canvas = document.createElement('canvas')
      this.canvas.style.position = 'absolute'
      this.canvas.style.pointerEvents = 'none'
      this.getPanes().overlayLayer.appendChild(this.canvas)
      // draw() only runs on zoom; redraw after panning to cover the new area
      this.idle = map.addListener('idle', () => this.draw())
    }

    onRemove() {
      this.idle.remove()
      this.canvas.remove()
    }

    draw() {
      const proj = this.getProjection()
      const bounds = map.getBounds()
      if (!proj || !bounds) return
      const ne = proj.fromLatLngToDivPixel(bounds.getNorthEast())
      const sw = proj.fromLatLngToDivPixel(bounds.getSouthWest())
      const left = Math.floor(sw.x - RADIUS)
      const top = Math.floor(ne.y - RADIUS)
      const width = Math.ceil(ne.x - sw.x + 2 * RADIUS)
      const height = Math.ceil(sw.y - ne.y + 2 * RADIUS)
      if (width <= 0 || height <= 0) return

      const canvas = this.canvas
      canvas.style.left = `${left}px`
      canvas.style.top = `${top}px`
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d', { willReadFrequently: true })

      points.forEach((p) => {
        const alpha = POINT_ALPHA * (p.weight ?? 1)
        if (alpha <= 0) return
        const px = proj.fromLatLngToDivPixel(new maps.LatLng(p.lat, p.lng))
        const x = px.x - left
        const y = px.y - top
        if (x < -RADIUS || y < -RADIUS || x > width + RADIUS || y > height + RADIUS)
          return
        const blob = ctx.createRadialGradient(x, y, 0, x, y, RADIUS)
        // Roughly gaussian falloff so overlapping points blend into soft gradients
        blob.addColorStop(0, `rgba(0,0,0,${alpha})`)
        blob.addColorStop(0.4, `rgba(0,0,0,${alpha * 0.55})`)
        blob.addColorStop(0.7, `rgba(0,0,0,${alpha * 0.18})`)
        blob.addColorStop(1, 'rgba(0,0,0,0)')
        ctx.fillStyle = blob
        ctx.fillRect(x - RADIUS, y - RADIUS, RADIUS * 2, RADIUS * 2)
      })

      const image = ctx.getImageData(0, 0, width, height)
      const d = image.data
      const palette = this.palette
      for (let i = 3; i < d.length; i += 4) {
        const a = d[i]
        if (!a) continue
        const o = a * 4
        d[i - 3] = palette[o]
        d[i - 2] = palette[o + 1]
        d[i - 1] = palette[o + 2]
        d[i] = Math.min(210, a * 3) // fade out softly at the edges
      }
      ctx.putImageData(image, 0, 0)
    }
  }

  const overlay = new Heatmap()
  overlay.setMap(map)
  return overlay
}

    